import { Component, Injector, ViewChild } from "@angular/core";
import { NonParticipantUserTypes } from "@common/ADAPT.Common.Model/embed/user-type";
import { Zone } from "@common/ADAPT.Common.Model/methodology/zone";
import { TeamType } from "@common/ADAPT.Common.Model/organisation/team-type";
import { TeamTypeCode } from "@common/ADAPT.Common.Model/organisation/team-type-code";
import { TeamTypeConfiguration } from "@common/ADAPT.Common.Model/organisation/team-type-configuration";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { BreezeEntityUtilities } from "@common/lib/data/breeze-entity-utilities";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { ConfigurationAuthService } from "@org-common/lib/configuration/configuration-auth.service";
import { EditTeamLocationsComponent } from "@org-common/lib/teams/edit-team-locations/edit-team-locations.component";
import { WorkflowStepComponent } from "@org-common/lib/workflow/workflow-component-registry";
import { AddTeamWorkflowStepBaseComponent } from "../add-team-workflow-step-base.component";

@WorkflowStepComponent("adapt-add-team-details-step")
@Component({
    selector: "adapt-add-team-details-step",
    templateUrl: "./add-team-details-step.component.html",
    standalone: false,
})
export class AddTeamDetailsStepComponent extends AddTeamWorkflowStepBaseComponent {
    public readonly ConfigureOrganisation = ConfigurationAuthService.ConfigureOrganisation;

    public readonly allowedUserTypes = NonParticipantUserTypes;

    // not in the template, passed to adaptTooltip as html
    public readonly ownerTeamTooltip = `<ul class="list-content mb-0">
        <li>Adds team guidance template to the team</li>
        <li>Adds default meetings (workshops, quarterly) to the team</li>
        <li>Enables Vision for the team</li>
        <li>Adds <b>Design</b> journey progress to the organisation</li>
        <li>Adds owner-related roles to the organisation</li>
    </ul>`;
    public readonly leadersTeamTooltip = `<ul class="list-content mb-0">
        <li>Adds <b>Build</b> journey progress to the organisation</li>
        <li>Set as the Leaders team for the Resilient Business Assessment, if not already set</li>
    </ul>`;

    public teamLeaderLabel$ = this.teamsService.getTeamLeaderLabel$();

    @ViewChild("editTeamLocations") public editTeamLocations!: EditTeamLocationsComponent;

    public canCreateOwnerTeam = false;
    public canCreateLeadersTeam = false;

    public createAsOwnerTeam = false;
    public createAsLeadershipTeam = false;

    public constructor(
        injector: Injector,
        private authService: AuthorisationService,
    ) {
        super(injector);
    }

    public get team() {
        return this.runData!.team;
    }

    public get entitiesAreUnmodifiedOrInvalid() {
        return this.team.name === ""
            || this.team.teamLeaderPerson === null
            || BreezeEntityUtilities.entitiesAreUnmodifiedOrInvalid(this.runData.entitiesToConfirm);
    }

    public get stepIsValid() {
        return !this.entitiesAreUnmodifiedOrInvalid && !this.submitting;
    }

    public get isCoachOrStakeholderManager() {
        const currentPerson = this.userService.currentPerson;
        return (currentPerson?.isStakeholderManager || currentPerson?.isCoach()) ?? false;
    }

    public get isOwnerTeam() {
        return this.team.teamType === TeamType.Owner;
    }

    public get isPrivateNonStakeholderManagerOrCoach() {
        const currentPerson = this.userService.currentPerson;
        return this.team.isPrivate && !(this.authService.isStakeholderManager() && !currentPerson?.getActiveConnections().length)
            && !currentPerson?.isCoach();
    }

    public async workflowStepOnInit() {
        super.workflowStepOnInit();

        const [activeOwnerTeams, leadershipTeam] = await Promise.all([
            this.teamsService.promiseToGetActiveOwnerTeams(),
            this.teamsService.promiseToGetLeadershipTeam(),
        ]);

        // if existing owner teams, don't offer owner option
        this.canCreateOwnerTeam = activeOwnerTeams.length === 0 || activeOwnerTeams[0] === this.team;

        // if existing leadership team, don't offer leadership option
        this.canCreateLeadersTeam = !leadershipTeam || leadershipTeam === this.team;

        // populate these checkboxes from the existing values
        this.createAsOwnerTeam = this.runData.team.teamType === TeamType.Owner;
        this.createAsLeadershipTeam = this.runData.team.teamType === TeamType.Leadership;
    }

    public async onOwnerTeamCheckChanged(checked: boolean) {
        this.createAsOwnerTeam = checked;
        const partialZoneLocation = { zone: Zone.HealthyCulture };

        if (checked) {
            this.createAsLeadershipTeam = false;
            this.team.name = this.team.name || "Owner";
            this.team.teamType = TeamType.Owner;
            this.team.isPrivate = true;
            await this.setTeamTypeConfiguration(TeamTypeCode.Leadership);
            await this.addDefaultLocation(partialZoneLocation);
        } else {
            this.team.teamType = TeamType.Standard;
            await this.setTeamTypeConfiguration(TeamTypeCode.Team);
            this.clearDefaultLocation(partialZoneLocation);
        }

        if (!this.runData.hasCustomisedFeatures) {
            this.setTeamDefaultFeatures();
        }

        this.onEntitiesChanged([this.team]);
        await this.editTeamLocations.refreshData();
    }


    public async onLeadershipTeamCheckChanged(checked: boolean) {
        this.createAsLeadershipTeam = checked;

        const teams = await this.teamsService.promiseToGetAllActiveTeams();
        const leadTheOrganisationTeam = teams.find((t) => t.name === "Lead the Organisation"
            && t.teamTypeConfiguration?.code === TeamTypeCode.KeyFunction);
        const partialLocation = leadTheOrganisationTeam
            ? { parentTeam: leadTheOrganisationTeam }
            : { zone: Zone.HealthyCulture };

        if (checked) {
            this.createAsOwnerTeam = false;
            this.team.name = this.team.name || "Organisation Leaders";
            this.team.teamType = TeamType.Leadership;
            await this.setTeamTypeConfiguration(TeamTypeCode.Leadership);
            await this.addDefaultLocation(partialLocation);
        } else {
            this.team.teamType = TeamType.Standard;
            await this.setTeamTypeConfiguration(TeamTypeCode.Team);
            this.clearDefaultLocation(partialLocation);
        }

        if (!this.runData.hasCustomisedFeatures) {
            this.setTeamDefaultFeatures();
        }

        this.onEntitiesChanged([this.team]);
        await this.editTeamLocations.refreshData();
    }

    public onPrivateTeamChanged(isPrivate: boolean) {
        this.team.isPrivate = isPrivate;
        this.onEntitiesChanged([this.team]);
    }

    public addEntitiesToConfirm(entities: IBreezeEntity[]) {
        this.onEntitiesChanged(entities);
    }

    public onTeamLocationEntityAdded(entity: IBreezeEntity) {
        // stop tracking the default location if the user manually removed it
        if (this.runData.defaultLocation === entity) {
            this.runData.defaultLocation = undefined;
        }

        this.onEntitiesChanged([entity]);
    }

    public async onTeamTypeChanged(teamTypeConfiguration: TeamTypeConfiguration) {
        await this.onTeamTypeConfigurationChange(teamTypeConfiguration);
        await this.editTeamLocations.refreshData();
    }

    public onTeamLeaderChanged(person?: Person) {
        if (!person) {
            return;
        }

        this.team.teamLeaderPerson = person;
        this.team.teamLeaderPersonId = person.personId;

        // if newly selected team leader is already a member, remove them as a team member
        if (this.runData.createTeamBindingModel.memberPersonIds.includes(person.personId)) {
            this.runData.createTeamBindingModel.memberPersonIds = this.runData.createTeamBindingModel.memberPersonIds
                .filter((id) => id !== person.personId);
        }

        this.onEntitiesChanged([this.team]);
    }

    private async setTeamTypeConfiguration(code: TeamTypeCode) {
        const configs = await this.teamsService.promiseToGetTeamTypeConfigurations();
        const config = configs.find((t) => t.code === code);
        if (config) {
            this.team.teamTypeConfiguration = config;
        }
    }
}
